import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtMoney, fmtDate, fmtDateTime, PAYMENT_MODES, PLATFORMS } from "@/lib/api";
import { exportPdf } from "@/lib/pdfExport";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Download, FileText } from "lucide-react";
import StatusPill from "@/components/StatusPill";
import { useAuth } from "@/contexts/AuthContext";
import PrintPageButton from "@/components/PrintPageButton";
import TxnTypeBadge from "@/components/TxnTypeBadge";

function toCsv(rows, columns) {
    const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [columns.map((c) => cell(c.label)).join(",")];
    rows.forEach((r) => lines.push(columns.map((c) => cell(typeof c.value === "function" ? c.value(r) : r[c.value])).join(",")));
    return lines.join("\n");
}
function download(csv, filename) {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

function ExportBar({ rows, columns, filename, title, filters, summary, disabled }) {
    const empty = disabled || !rows?.length;
    const baseName = (filename || title || "report").replace(/\.csv$/i, "");
    return (
        <div className="inline-flex gap-2">
            <Button
                size="sm"
                variant="outline"
                disabled={empty}
                onClick={() => download(toCsv(rows, columns), `${baseName}.csv`)}
                data-testid="report-export-btn"
            >
                <Download size={14} className="mr-1" /> CSV
            </Button>
            <Button
                size="sm"
                variant="outline"
                disabled={empty}
                onClick={() => exportPdf({ title, filters, summary, columns, rows, filename: baseName })}
                data-testid="report-export-pdf-btn"
            >
                <FileText size={14} className="mr-1" /> PDF
            </Button>
        </div>
    );
}

// ---------- Report: Monthly Expense ----------
function MonthlyExpense({ cards, canExport }) {
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
    const [cardId, setCardId] = useState("all");
    const [bp, setBp] = useState("all");
    const [platform, setPlatform] = useState("all");
    const [rows, setRows] = useState([]);
    useEffect(() => {
        const p = { month };
        if (cardId !== "all") p.card_id = cardId;
        if (bp !== "all") p.business_personal = bp;
        api.get("/reports/monthly-spend", { params: p }).then((r) => setRows(r.data));
    }, [month, cardId, bp]);
    const cardMap = useMemo(() => Object.fromEntries(cards.map((c) => [c.id, c])), [cards]);
    const filtered = platform === "all" ? rows : rows.filter((r) => r.platform === platform);
    const total = filtered.reduce((s, r) => s + Number(r.amount || 0), 0);
    const grossTotal = filtered.reduce((s, r) => s + Number(r.gross_amount || r.amount || 0), 0);
    const cardLabel = cardId === "all" ? "All cards" : (() => { const c = cardMap[cardId]; return c ? `${c.bank_name} ••••${c.last_4}` : cardId; })();
    const cols = [
        { label: "Date", value: (r) => fmtDate(r.date) },
        { label: "Merchant", value: "merchant" },
        { label: "Platform", value: (r) => r.platform || "—" },
        { label: "Card", value: (r) => { const c = cardMap[r.card_id]; return c ? `${c.bank_name} ${c.card_name} ••••${c.last_4}` : ""; } },
        { label: "Category", value: "category" },
        { label: "B/P", value: "business_personal" },
        { label: "Type", value: (r) => r.transaction_type || "Debit" },
        { label: "Gross", value: "gross_amount", money: true },
        { label: "On Card", value: "amount", money: true },
    ];
    const filters = { Month: month, Card: cardLabel, "Business/Personal": bp, Platform: platform };
    const summary = `${filtered.length} entries · Card total ${fmtMoney(total)}${grossTotal > total ? ` · saved ${fmtMoney(grossTotal - total)}` : ""}`;
    return (
        <div>
            <div className="flex flex-wrap gap-3 mb-4 items-center">
                <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" />
                <Select value={cardId} onValueChange={setCardId}>
                    <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All cards</SelectItem>
                        {cards.map((c) => <SelectItem key={c.id} value={c.id}>{c.bank_name} · •••• {c.last_4}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={bp} onValueChange={setBp}>
                    <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="Business">Business</SelectItem><SelectItem value="Personal">Personal</SelectItem></SelectContent>
                </Select>
                <Select value={platform} onValueChange={setPlatform}>
                    <SelectTrigger className="w-[170px]" data-testid="report-platform-filter"><SelectValue placeholder="Any platform" /></SelectTrigger>
                    <SelectContent className="max-h-64">
                        <SelectItem value="all">Any platform</SelectItem>
                        {PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                </Select>
                <span className="text-sm text-stone-500 ml-auto">
                    {filtered.length} entries · Card {fmtMoney(total)}
                    {grossTotal > total && <span className="text-emerald-700 ml-1">· saved {fmtMoney(grossTotal - total)}</span>}
                </span>
                <ExportBar
                    rows={filtered} columns={cols}
                    filename={`monthly_expense_${month}${platform === "all" ? "" : "_" + platform.toLowerCase().replace(/\s+/g, "-")}`}
                    title="Monthly Expense" filters={filters} summary={summary}
                    disabled={!canExport}
                />
            </div>
            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden overflow-x-auto">
                <table className="dense-table w-full">
                    <thead><tr><th>Date</th><th>Merchant</th><th>Platform</th><th>Card</th><th>Category</th><th>B/P</th><th>Type</th><th className="text-right">Gross</th><th className="text-right">On Card</th></tr></thead>
                    <tbody>
                        {filtered.map((r) => { const c = cardMap[r.card_id]; const isCredit = (r.transaction_type || "Debit") === "Credit"; return (
                            <tr key={r.id} data-testid={`report-monthly-row-${r.id}`}>
                                <td className="tabular-nums">{fmtDate(r.date)}</td>
                                <td>{r.merchant}</td>
                                <td className="text-xs text-stone-600">{r.platform || "—"}</td>
                                <td>{c ? `${c.bank_name} · •••• ${c.last_4}` : "—"}</td>
                                <td>{r.category}</td>
                                <td>{r.business_personal}</td>
                                <td><TxnTypeBadge value={r.transaction_type} /></td>
                                <td className="text-right tabular-nums text-stone-500">{r.gross_amount ? fmtMoney(r.gross_amount) : "—"}</td>
                                <td className={`text-right tabular-nums font-medium ${isCredit ? "text-emerald-700" : ""}`}>{isCredit ? "+ " : ""}{fmtMoney(r.amount)}</td>
                            </tr>
                        );})}
                        {filtered.length === 0 && <tr><td colSpan={9} className="text-center text-stone-500 py-10">No entries.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------- Report: Card Master ----------
function CardMasterReport({ canExport }) {
    const [status, setStatus] = useState("all");
    const [bank, setBank] = useState("");
    const [rows, setRows] = useState([]);
    useEffect(() => {
        const p = {};
        if (status !== "all") p.status = status;
        if (bank) p.bank = bank;
        api.get("/reports/card-master", { params: p }).then((r) => setRows(r.data.rows || []));
    }, [status, bank]);
    const cols = [
        { label: "Bank", value: "bank_name" },
        { label: "Card", value: "card_name" },
        { label: "Last 4", value: (r) => `••••${r.last_4}` },
        { label: "Holder", value: "card_holder_name" },
        { label: "Network", value: "card_network" },
        { label: "Credit Limit", value: "credit_limit", money: true },
        { label: "Due Day", value: "payment_due_day", align: "right" },
        { label: "Status", value: "status" },
    ];
    const filters = { Status: status, Bank: bank || "—" };
    return (
        <div>
            <div className="flex flex-wrap gap-3 mb-4 items-center">
                <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="Active">Active</SelectItem>
                        <SelectItem value="Closed">Closed</SelectItem>
                        <SelectItem value="Blocked">Blocked</SelectItem>
                        <SelectItem value="Lost">Lost</SelectItem>
                        <SelectItem value="Replaced">Replaced</SelectItem>
                    </SelectContent>
                </Select>
                <Input placeholder="Filter by bank…" value={bank} onChange={(e) => setBank(e.target.value)} className="w-56" />
                <span className="text-sm text-stone-500 ml-auto">{rows.length} cards</span>
                <ExportBar
                    rows={rows} columns={cols} filename="card_master"
                    title="Card Master" filters={filters} summary={`${rows.length} cards`}
                    disabled={!canExport}
                />
            </div>
            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden overflow-x-auto">
                <table className="dense-table w-full">
                    <thead><tr><th>Bank</th><th>Card</th><th>Last 4</th><th>Holder</th><th>Network</th><th className="text-right">Limit</th><th>Due Day</th><th>Status</th></tr></thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id}>
                                <td className="font-medium">{r.bank_name}</td>
                                <td>{r.card_name}</td>
                                <td className="font-mono-figure">••••{r.last_4}</td>
                                <td>{r.card_holder_name || "—"}</td>
                                <td>{r.card_network}</td>
                                <td className="text-right tabular-nums">{fmtMoney(r.credit_limit)}</td>
                                <td className="tabular-nums">{r.payment_due_day || "—"}</td>
                                <td><StatusPill status={r.status} /></td>
                            </tr>
                        ))}
                        {rows.length === 0 && <tr><td colSpan={8} className="text-center text-stone-500 py-10">No cards.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------- Report: Card-wise Spend ----------
function CardSpend({ canExport }) {
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
    const [data, setData] = useState({ rows: [], grand_total: 0 });
    useEffect(() => { api.get("/reports/card-spend", { params: { month } }).then((r) => setData(r.data)); }, [month]);
    const cols = [
        { label: "Bank", value: "bank_name" }, { label: "Card", value: "card_name" }, { label: "Last 4", value: (r) => `••••${r.last_4}` },
        { label: "Count", value: "count", align: "right" }, { label: "Business", value: "business", money: true },
        { label: "Personal", value: "personal", money: true }, { label: "Total", value: "total_spend", money: true },
    ];
    const filters = { Month: month };
    const summary = `${data.rows.length} cards · Grand total ${fmtMoney(data.grand_total)}`;
    return (
        <div>
            <div className="flex flex-wrap gap-3 mb-4 items-center">
                <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" />
                <span className="text-sm text-stone-500 ml-auto">Total: {fmtMoney(data.grand_total)}</span>
                <ExportBar
                    rows={data.rows} columns={cols} filename={`card_spend_${month}`}
                    title="Card-wise Spend" filters={filters} summary={summary}
                    disabled={!canExport}
                />
            </div>
            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden overflow-x-auto">
                <table className="dense-table w-full">
                    <thead><tr><th>Bank</th><th>Card</th><th>Last 4</th><th className="text-right">Count</th><th className="text-right">Business</th><th className="text-right">Personal</th><th className="text-right">Total</th></tr></thead>
                    <tbody>
                        {data.rows.map((r) => (
                            <tr key={r.card_id}>
                                <td>{r.bank_name}</td>
                                <td>{r.card_name}</td>
                                <td className="font-mono-figure">••••{r.last_4}</td>
                                <td className="text-right tabular-nums">{r.count}</td>
                                <td className="text-right tabular-nums">{fmtMoney(r.business)}</td>
                                <td className="text-right tabular-nums">{fmtMoney(r.personal)}</td>
                                <td className="text-right tabular-nums font-medium">{fmtMoney(r.total_spend)}</td>
                            </tr>
                        ))}
                        {data.rows.length === 0 && <tr><td colSpan={7} className="text-center text-stone-500 py-10">No spend this month.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------- Report: Bank-wise Outstanding ----------
function BankOutstanding({ canExport }) {
    const [data, setData] = useState({ rows: [], total_outstanding: 0 });
    useEffect(() => { api.get("/reports/bank-outstanding").then((r) => setData(r.data)); }, []);
    const cols = [
        { label: "Bank", value: "bank" }, { label: "Cards", value: "cards", align: "right" },
        { label: "Limit", value: "limit", money: true }, { label: "Outstanding", value: "outstanding", money: true },
        { label: "Utilization %", value: (r) => `${r.utilization_pct}%`, align: "right" },
    ];
    const summary = `${data.rows.length} banks · Total outstanding ${fmtMoney(data.total_outstanding)}`;
    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                <span className="text-sm text-stone-500">Total outstanding: {fmtMoney(data.total_outstanding)}</span>
                <ExportBar
                    rows={data.rows} columns={cols} filename="bank_outstanding"
                    title="Bank-wise Outstanding" summary={summary}
                    disabled={!canExport}
                />
            </div>
            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden overflow-x-auto">
                <table className="dense-table w-full">
                    <thead><tr><th>Bank</th><th className="text-right">Cards</th><th className="text-right">Limit</th><th className="text-right">Outstanding</th><th className="text-right">Utilization</th></tr></thead>
                    <tbody>
                        {data.rows.map((r) => (
                            <tr key={r.bank}>
                                <td className="font-medium">{r.bank}</td>
                                <td className="text-right tabular-nums">{r.cards}</td>
                                <td className="text-right tabular-nums">{fmtMoney(r.limit)}</td>
                                <td className="text-right tabular-nums font-medium">{fmtMoney(r.outstanding)}</td>
                                <td className="text-right tabular-nums">
                                    <span className={r.utilization_pct >= 70 ? "text-red-700" : r.utilization_pct >= 50 ? "text-amber-700" : "text-stone-600"}>{r.utilization_pct}%</span>
                                </td>
                            </tr>
                        ))}
                        {data.rows.length === 0 && <tr><td colSpan={5} className="text-center text-stone-500 py-10">No cards.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------- Report: Payments ----------
function PaymentsReport({ cards, canExport }) {
    const [month, setMonth] = useState("");
    const [posted, setPosted] = useState("all");
    const [mode, setMode] = useState("all");
    const [data, setData] = useState({ rows: [], total_paid: 0 });
    useEffect(() => {
        const p = {};
        if (month) p.month = month;
        if (posted !== "all") p.posted = posted === "yes";
        api.get("/reports/payments", { params: p }).then((r) => setData(r.data));
    }, [month, posted]);
    const filteredRows = mode === "all" ? data.rows : data.rows.filter((r) => r.payment_mode === mode);
    const filteredTotal = filteredRows.reduce((s, r) => s + (Number(r.amount_paid) || 0), 0);
    const cols = [
        { label: "Date", value: (r) => fmtDate(r.payment_date) },
        { label: "Card", value: (r) => `${r.bank_name} ••••${r.last_4}` },
        { label: "Amount", value: "amount_paid", money: true },
        { label: "Mode", value: "payment_mode" },
        { label: "UTR", value: "utr" },
        { label: "Reflected", value: (r) => r.posted ? "Yes" : "No" },
    ];
    const filters = { Month: month || "All", Reflected: posted === "all" ? "All" : (posted === "yes" ? "Reflected" : "Pending reflection"), Mode: mode };
    const summary = `${filteredRows.length} payments · ${fmtMoney(filteredTotal)}`;
    return (
        <div>
            <div className="flex flex-wrap gap-3 mb-4 items-center">
                <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" />
                <Select value={posted} onValueChange={setPosted}>
                    <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="yes">Reflected</SelectItem><SelectItem value="no">Pending reflection</SelectItem></SelectContent>
                </Select>
                <Select value={mode} onValueChange={setMode}>
                    <SelectTrigger className="w-[160px]" data-testid="report-payment-mode-filter"><SelectValue placeholder="Any mode" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Any mode</SelectItem>
                        {PAYMENT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                </Select>
                <span className="text-sm text-stone-500 ml-auto">{filteredRows.length} payments · {fmtMoney(filteredTotal)}</span>
                <ExportBar
                    rows={filteredRows} columns={cols}
                    filename={`payments_${month || "all"}${mode === "all" ? "" : "_" + mode.toLowerCase().replace(/\s+/g, "-")}`}
                    title="Payments" filters={filters} summary={summary}
                    disabled={!canExport}
                />
            </div>
            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden overflow-x-auto">
                <table className="dense-table w-full">
                    <thead><tr><th>Date</th><th>Card</th><th className="text-right">Amount</th><th>Mode</th><th>UTR</th><th>Reflected</th></tr></thead>
                    <tbody>
                        {filteredRows.map((r) => (
                            <tr key={r.id}>
                                <td className="tabular-nums">{fmtDate(r.payment_date)}</td>
                                <td>{r.bank_name} · ••••{r.last_4}</td>
                                <td className="text-right tabular-nums">{fmtMoney(r.amount_paid)}</td>
                                <td>{r.payment_mode}</td>
                                <td className="font-mono-figure text-xs">{r.utr || "—"}</td>
                                <td>{r.posted ? <span className="text-emerald-700 text-xs">Yes</span> : <span className="text-amber-700 text-xs">Pending</span>}</td>
                            </tr>
                        ))}
                        {filteredRows.length === 0 && <tr><td colSpan={6} className="text-center text-stone-500 py-10">No payments.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------- Report: Due Dates ----------
function DueDates({ canExport }) {
    const [data, setData] = useState({ rows: [] });
    useEffect(() => { api.get("/reports/due-dates").then((r) => setData(r.data)); }, []);
    const cols = [
        { label: "Bank", value: "bank_name" }, { label: "Card", value: "card_name" }, { label: "Last 4", value: (r) => `••••${r.last_4}` },
        { label: "Holder", value: "card_holder" }, { label: "Due Date", value: "due_date" },
        { label: "Days Left", value: "days_left", align: "right" }, { label: "Outstanding", value: "outstanding", money: true },
        { label: "Statement", value: (r) => r.statement_uploaded ? "Uploaded" : "Missing" }, { label: "Status", value: "status" },
    ];
    const summary = `${data.rows?.length || 0} active cards`;
    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                <span className="text-sm text-stone-500">{data.rows?.length || 0} active cards</span>
                <ExportBar
                    rows={data.rows || []} columns={cols} filename="due_dates"
                    title="Due Dates" summary={summary}
                    disabled={!canExport}
                />
            </div>
            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden overflow-x-auto">
                <table className="dense-table w-full">
                    <thead><tr><th>Bank</th><th>Card</th><th>Last 4</th><th>Holder</th><th>Due Date</th><th className="text-right">Days Left</th><th className="text-right">Outstanding</th><th>Statement</th><th>Status</th></tr></thead>
                    <tbody>
                        {(data.rows || []).map((r) => (
                            <tr key={r.card_id}>
                                <td>{r.bank_name}</td>
                                <td>{r.card_name}</td>
                                <td className="font-mono-figure">••••{r.last_4}</td>
                                <td>{r.card_holder || "—"}</td>
                                <td className="tabular-nums">{r.due_date || "—"}</td>
                                <td className="text-right tabular-nums">{r.days_left ?? "—"}</td>
                                <td className="text-right tabular-nums">{fmtMoney(r.outstanding)}</td>
                                <td className="text-xs">{r.statement_uploaded ? <span className="text-emerald-700">Uploaded</span> : <span className="text-amber-700">Missing</span>}</td>
                                <td><StatusPill status={r.status} /></td>
                            </tr>
                        ))}
                        {!data.rows?.length && <tr><td colSpan={9} className="text-center text-stone-500 py-10">No active cards.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------- Report: Missing Statements ----------
function MissingStatements({ canExport }) {
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
    const [data, setData] = useState({ rows: [] });
    useEffect(() => { api.get("/reports/missing-statements", { params: { month } }).then((r) => setData(r.data)); }, [month]);
    const cols = [
        { label: "Bank", value: "bank_name" }, { label: "Card", value: "card_name" }, { label: "Last 4", value: (r) => `••••${r.last_4}` },
        { label: "Expected", value: "expected_statement_date" }, { label: "Days Overdue", value: "days_overdue", align: "right" },
    ];
    const filters = { Month: month };
    const summary = `${data.count || 0} missing`;
    return (
        <div>
            <div className="flex flex-wrap gap-3 mb-4 items-center">
                <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" />
                <span className="text-sm text-stone-500 ml-auto">{data.count || 0} missing</span>
                <ExportBar
                    rows={data.rows || []} columns={cols} filename={`missing_statements_${month}`}
                    title="Missing Statements" filters={filters} summary={summary}
                    disabled={!canExport}
                />
            </div>
            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden overflow-x-auto">
                <table className="dense-table w-full">
                    <thead><tr><th>Bank</th><th>Card</th><th>Last 4</th><th>Expected</th><th className="text-right">Days Overdue</th></tr></thead>
                    <tbody>
                        {(data.rows || []).map((r) => (
                            <tr key={r.card_id}>
                                <td>{r.bank_name}</td>
                                <td>{r.card_name}</td>
                                <td className="font-mono-figure">••••{r.last_4}</td>
                                <td className="tabular-nums">{r.expected_statement_date || "—"}</td>
                                <td className={`text-right tabular-nums ${r.days_overdue > 0 ? "text-red-700" : ""}`}>{r.days_overdue ?? 0}</td>
                            </tr>
                        ))}
                        {!data.rows?.length && <tr><td colSpan={5} className="text-center text-stone-500 py-10">All statements uploaded.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------- Report: Audit Log ----------
function AuditLogReport({ canExport }) {
    const [entity, setEntity] = useState("all");
    const [action, setAction] = useState("all");
    const [data, setData] = useState({ rows: [] });
    useEffect(() => {
        const p = {};
        if (entity !== "all") p.entity = entity;
        if (action !== "all") p.action = action;
        api.get("/reports/audit-log", { params: p }).then((r) => setData(r.data));
    }, [entity, action]);
    const cols = [
        { label: "Time", value: (r) => fmtDateTime(r.timestamp) },
        { label: "User", value: "user_email" }, { label: "Role", value: "user_role" },
        { label: "Action", value: "action" }, { label: "Entity", value: "entity" }, { label: "Entity ID", value: "entity_id" },
    ];
    const filters = { Entity: entity, Action: action };
    const summary = `${data.count || 0} entries`;
    return (
        <div>
            <div className="flex flex-wrap gap-3 mb-4 items-center">
                <Select value={entity} onValueChange={setEntity}>
                    <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All entities</SelectItem>
                        <SelectItem value="card">Cards</SelectItem><SelectItem value="expense">Expenses</SelectItem>
                        <SelectItem value="statement">Statements</SelectItem><SelectItem value="payment">Payments</SelectItem>
                        <SelectItem value="user">Users</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={action} onValueChange={setAction}>
                    <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All actions</SelectItem>
                        <SelectItem value="created">Created</SelectItem><SelectItem value="updated">Updated</SelectItem><SelectItem value="deleted">Deleted</SelectItem>
                        <SelectItem value="uploaded">Uploaded</SelectItem><SelectItem value="paid">Paid</SelectItem>
                    </SelectContent>
                </Select>
                <span className="text-sm text-stone-500 ml-auto">{data.count || 0} entries</span>
                <ExportBar
                    rows={data.rows || []} columns={cols} filename="audit_log"
                    title="Audit Log" filters={filters} summary={summary}
                    disabled={!canExport}
                />
            </div>
            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden overflow-x-auto">
                <table className="dense-table w-full">
                    <thead><tr><th>Time</th><th>User</th><th>Role</th><th>Action</th><th>Entity</th><th>Changes</th></tr></thead>
                    <tbody>
                        {(data.rows || []).map((r) => (
                            <tr key={r.id}>
                                <td className="tabular-nums text-xs whitespace-nowrap">{fmtDateTime(r.timestamp)}</td>
                                <td>{r.user_email || "—"}</td>
                                <td className="text-xs uppercase tracking-widest text-stone-500">{r.user_role || "—"}</td>
                                <td>{r.action}</td>
                                <td className="text-[11px] uppercase tracking-widest">{r.entity}</td>
                                <td className="text-[11px] text-stone-500 max-w-[420px] truncate">
                                    {r.diff ? Object.entries(r.diff).map(([k, v]) => `${k}: ${JSON.stringify(v.old)} → ${JSON.stringify(v.new)}`).join(" · ") : (r.details ? JSON.stringify(r.details) : "—")}
                                </td>
                            </tr>
                        ))}
                        {!data.rows?.length && <tr><td colSpan={6} className="text-center text-stone-500 py-10">No audit entries.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// Map sidebar deep-links (?tab=monthly|cards|cash|expenses|networth|missing)
// to the tab keys defined below.
const TAB_ALIASES = {
    monthly:  "monthly-expense",
    expenses: "monthly-expense",
    cards:    "card-master",
    cash:     "card-spend",
    networth: "bank-outstanding",
    missing:  "missing-statements",
    audit:    "audit-log",
    payments: "payments",
    due:      "due-dates",
};

export default function Reports() {
    const { hasRole } = useAuth();
    const [cards, setCards] = useState([]);
    const [sp, setSp] = useSearchParams();
    useEffect(() => { api.get("/cards").then((r) => setCards(r.data)); }, []);
    const canExport = hasRole("admin", "finance");
    const tabParam = sp.get("tab");
    const initialTab = (tabParam && (TAB_ALIASES[tabParam] || tabParam)) || "monthly-expense";
    return (
        <div data-testid="reports-page">
            <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="eyebrow">Reports</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Every angle of your ledger.</h1>
                    <p className="text-sm text-stone-500 mt-3">Eight built-in reports · export CSV or branded PDF where permitted.</p>
                </div>
                <PrintPageButton />
            </div>
            <Tabs
                value={initialTab}
                onValueChange={(v) => { const next = new URLSearchParams(sp); next.set("tab", v); setSp(next, { replace: true }); }}
            >
                <TabsList className="mb-4 flex-wrap gap-1">
                    <TabsTrigger value="monthly-expense" data-testid="tab-monthly-expense">Monthly Expense</TabsTrigger>
                    <TabsTrigger value="card-master" data-testid="tab-card-master">Card Master</TabsTrigger>
                    <TabsTrigger value="card-spend" data-testid="tab-card-spend">Card-wise Spend</TabsTrigger>
                    <TabsTrigger value="bank-outstanding" data-testid="tab-bank-outstanding">Bank Outstanding</TabsTrigger>
                    <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
                    <TabsTrigger value="due-dates" data-testid="tab-due-dates">Due Dates</TabsTrigger>
                    <TabsTrigger value="missing-statements" data-testid="tab-missing-statements">Missing Statements</TabsTrigger>
                    <TabsTrigger value="audit-log" data-testid="tab-audit-log">Audit Log</TabsTrigger>
                </TabsList>
                <TabsContent value="monthly-expense"><MonthlyExpense cards={cards} canExport={canExport} /></TabsContent>
                <TabsContent value="card-master"><CardMasterReport canExport={canExport} /></TabsContent>
                <TabsContent value="card-spend"><CardSpend canExport={canExport} /></TabsContent>
                <TabsContent value="bank-outstanding"><BankOutstanding canExport={canExport} /></TabsContent>
                <TabsContent value="payments"><PaymentsReport cards={cards} canExport={canExport} /></TabsContent>
                <TabsContent value="due-dates"><DueDates canExport={canExport} /></TabsContent>
                <TabsContent value="missing-statements"><MissingStatements canExport={canExport} /></TabsContent>
                <TabsContent value="audit-log"><AuditLogReport canExport={canExport} /></TabsContent>
            </Tabs>
        </div>
    );
}
